import type { Ring, Vec2, Vec3 } from '../../../../geo/types';
import { orientedFace, planPoint } from '../../solids';
import { pointedArchRise, pointedWindow } from '../../panels';
import { gableWall, rectWall, roofUv } from '../masonry';
import { fittedButtress } from './oudeKerkButtress';
import { buildHallAisle } from '../churchSideParts';
import { ridgeTurret } from '../gothic/ridgeTurret';
import type { OudeKerkBuild } from './oudeKerkParts';
import { OUDE_KERK } from './oudeKerkPlan';

/**
 * The hall choir with its ambulatory of c.1445-55 under one roof, as the 1544 woodcut draws it: bays
 * of 5, 4.4, 4.2 and 4.2 m, side windows 2.97 m and ambulatory windows 3.85 m wide (Janse 2004);
 * buttresses faced with Gobertange stone under stone copings at the bays and at the apse's corners;
 * the roof turret over its westernmost bay (pre-1545 images); the sacristy of c.1516 against its
 * south side. The windows' sills and the buttresses' size are not recorded.
 */
const CHOIR = {
  sideWindow: 2.97,
  apseWindow: 3.85,
  apseWindowShare: 0.6,
  sill: 3.5,
  topBelowEave: 1.0,
  /* As deep as the trace leaves room for, which draws the choir without its buttresses. */
  buttress: { width: 0.8, depth: 1.2, drop: 0.8, pinnacle: 0.6 },
} as const;

function window(centre: number, width: number, eaveY: number): Ring {
  const top = eaveY - CHOIR.topBelowEave;
  return pointedWindow(centre, CHOIR.sill, width, top - pointedArchRise(width), 3);
}

/** The bay boundaries from the choir's west end to the apse chord. */
function bayBounds(s: OudeKerkBuild): number[] {
  const c = s.plan.choir;
  const bays = [c.u0];
  for (const b of OUDE_KERK.choir.bays) bays.push(bays[bays.length - 1]! + b);
  bays[bays.length - 1] = c.chordU;
  return bays;
}

function buildWalls(s: OudeKerkBuild): void {
  const c = s.plan.choir;
  const bays = bayBounds(s);
  const centres = bays.slice(1).map((u, k) => (bays[k]! + u) / 2);
  const south = centres.map((u) => window(u - c.u0, CHOIR.sideWindow, c.eaveY));
  const north = centres.map((u) => window(c.chordU - u, CHOIR.sideWindow, c.eaveY));
  rectWall(s.builder, s.stone, s.frame, [[c.u0, c.vS], [c.chordU, c.vS]], [0, c.eaveY], south);
  c.apse.slice(1).forEach((p, i) => {
    const q = c.apse[i]!;
    const facet = Math.hypot(p[0] - q[0], p[1] - q[1]);
    const width = Math.min(CHOIR.apseWindow, CHOIR.apseWindowShare * facet);
    rectWall(s.builder, s.stone, s.frame, [q, p], [0, c.eaveY], [window(facet / 2, width, c.eaveY)]);
  });
  rectWall(s.builder, s.stone, s.frame, [[c.chordU, c.vN], [c.u0, c.vN]], [0, c.eaveY], north);
  gableWall(s.builder, s.stone, s.frame, [[c.u0, c.vN], [c.u0, c.vS]], { eaveY: c.eaveY, apexY: c.ridgeY });
}

/** The two slopes to the chord and a facet from each side of the apse up to the ridge's end. */
function buildRoof(s: OudeKerkBuild): void {
  const c = s.plan.choir;
  const eave = (p: Vec2): Vec3 => planPoint(p[0], c.eaveY, p[1]);
  const ridge = (u: number): Vec3 => planPoint(u, c.ridgeY, c.vc);
  const face = (points: readonly Vec3[], outward: Vec3): void => { orientedFace(s.builder, s.stone.roof, s.frame, points, outward, roofUv(points[0]!, points[1]!, points[points.length - 1]!)); };
  face([eave([c.u0, c.vS]), eave([c.chordU, c.vS]), ridge(c.chordU), ridge(c.u0)], [0, 1, 1]);
  face([eave([c.chordU, c.vN]), eave([c.u0, c.vN]), ridge(c.u0), ridge(c.chordU)], [0, 1, -1]);
  c.apse.slice(1).forEach((p, i) => {
    const q = c.apse[i]!;
    const out: Vec3 = [p[1] - q[1], 1, p[0] - q[0]];
    face([eave(q), eave(p), ridge(c.chordU)], out);
  });
}

function buildButtresses(s: OudeKerkBuild): void {
  const c = s.plan.choir;
  const b = CHOIR.buttress;
  const spec = { width: b.width, depth: b.depth, height: c.eaveY - b.drop, pinnacle: b.pinnacle };
  for (const u of bayBounds(s).slice(1, -1)) {
    fittedButtress(s, [u, c.vS], [0, -1], spec);
    fittedButtress(s, [u, c.vN], [0, 1], spec);
  }
  c.apse.slice(1, -1).forEach((p, i) => {
    const q = c.apse[i]!;
    const r = c.apse[i + 2]!;
    const outward: Vec2 = [r[1] - q[1], q[0] - r[0]];
    const length = Math.hypot(outward[0], outward[1]);
    fittedButtress(s, p, [outward[0] / length, outward[1] / length], spec);
  });
}

function buildSacristy(s: OudeKerkBuild): void {
  const r = s.plan.sacristy;
  buildHallAisle(s.builder, s.stone, s.frame, { side: -1, u0: r.u0, u1: r.u1, inner: r.v1, outer: r.v0, eaveY: r.eaveY, topY: r.ridgeY, roof: 'gable' });
}

export function buildOudeKerkChoir(s: OudeKerkBuild): void {
  const c = s.plan.choir;
  buildWalls(s);
  buildRoof(s);
  buildButtresses(s);
  buildSacristy(s);
  ridgeTurret(s.builder, { lantern: s.lead, needle: s.stone.roof, finial: s.stone.wall }, s.frame, { center: [c.turret.u, c.vc], footY: c.turret.footY, topY: c.turret.topY });
}
